// Org-level default currency. One code per org. Drives the prefill on the
// "+ New lead" form and is the fallback when a lead doesn't carry its own
// currency. Per-lead overrides still work — leads carry their currency on
// the lead doc, so changing this here does not retro-update old leads.

import React, { useEffect, useState } from "react";
import { DollarSign, Check, Loader2 } from "lucide-react";
import { Select as AntSelect } from "antd";
import toast from "react-hot-toast";
import { Button, Badge } from "../../atoms";
import { useOrgCurrency, useUpdateOrgCurrency } from "../../hooks/useOrgCurrency";
import { CURRENCY_OPTIONS, getCurrencySymbol, DEFAULT_CURRENCY } from "../../lib/currency";

export default function CurrencySection() {
  const { currency, isLoading, isError, error } = useOrgCurrency();
  const updateMut = useUpdateOrgCurrency();
  const [draft, setDraft] = useState(currency || DEFAULT_CURRENCY);

  useEffect(() => { setDraft(currency || DEFAULT_CURRENCY); }, [currency]);

  const dirty = draft && draft !== currency;

  const save = async () => {
    try {
      await updateMut.mutateAsync(draft);
      toast.success(`Default currency set to ${draft}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  return (
    <section style={{ background: "var(--color-bg-surface)", border: "1px solid var(--color-border-subtle)", borderRadius: 8, padding: 20 }}>
      <header style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DollarSign size={15} style={{ color: "var(--crm-primary)" }} />
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Default currency</h2>
          {currency && (
            <Badge tone="neutral">
              {getCurrencySymbol(currency)} {currency}
            </Badge>
          )}
        </div>
        <p style={{ margin: "4px 0 0", fontSize: 13, color: "var(--color-text-secondary)" }}>
          The currency used to prefill new leads and format deal values when a lead doesn't carry its own. Per-lead overrides still work.
        </p>
      </header>

      {isLoading && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, color: "var(--color-text-secondary)", fontSize: 13 }}>
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      )}

      {isError && (
        <div style={{ padding: 12, background: "var(--crm-danger-soft)", color: "var(--crm-danger)", borderRadius: 8, fontSize: 13 }}>
          Couldn't load currency setting: {error?.response?.data?.error || error?.message || "unknown error"}
        </div>
      )}

      {!isLoading && !isError && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <AntSelect
            value={draft}
            onChange={(v) => setDraft(v)}
            options={CURRENCY_OPTIONS}
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 280, height: 36 }}
            disabled={updateMut.isPending}
          />
          <Button
            onClick={save}
            disabled={!dirty || updateMut.isPending}
            leftIcon={updateMut.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
          >
            {updateMut.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
          </Button>
        </div>
      )}
    </section>
  );
}
